/**
 * Kódból épített, alacsony poligonszámú modellek.
 *
 * Miért nem sprite: 5 ellenségtípus × 5 fázis × 8 nézeti irány ≈ 160 kézzel
 * kódolt rajz lenne, mindegyik külön esély a csúnyaságra, és iterálhatatlan.
 * Egy hierarchikus modellnél az animáció egy szinuszfüggvény, a világ fényével
 * egységesen világítódik, és nincs nyolcirány-probléma.
 *
 * A közös összeszerelő (`assemble`) garantálja, hogy minden figura EGY
 * stílusban legyen; a különbséget az arányok és a sziluett adja — ez volt
 * X jogos követelménye a `04-C-szintezis.md`-ben.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { RAMPS } from './palette'

// ---------------------------------------------------------------------------
// Anyagok — közös készlet, hogy ne szaporodjanak a draw call-ok
// ---------------------------------------------------------------------------

/**
 * A merge-elt testrészek KÖZÖS anyaga. A színt vertex-attribútum hordozza,
 * így egy rig-csomópont (törzs, fej, kar, láb) mindössze egyetlen rajzolási
 * hívás — szemben a ~20-szal, ha minden doboz külön mesh maradna.
 */
const SOLID_MATERIAL = new THREE.MeshLambertMaterial({
  vertexColors: true,
  flatShading: true,
})

const matCache = new Map<number, THREE.MeshLambertMaterial>()

function mat(color: number): THREE.MeshLambertMaterial {
  let m = matCache.get(color)
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color, flatShading: true })
    matCache.set(color, m)
    m.name = `solid:${color.toString(16)}`
  }
  return m
}

const emissiveCache = new Map<number, THREE.MeshBasicMaterial>()

function glow(color: number): THREE.MeshBasicMaterial {
  let m = emissiveCache.get(color)
  if (!m) {
    m = new THREE.MeshBasicMaterial({ color, toneMapped: false })
    emissiveCache.set(color, m)
  }
  return m
}

// ---------------------------------------------------------------------------
// Építő-primitívek
// ---------------------------------------------------------------------------

interface BoxSpec {
  /** Méret (szélesség, magasság, mélység). */
  s: [number, number, number]
  /** Pozíció a szülőhöz képest. */
  p: [number, number, number]
  color: number
  /** Elforgatás radiánban. */
  r?: [number, number, number]
  /** Önfényes rész (szem, energiamag) — nem kap árnyékot. */
  emissive?: boolean
  /** Felső lap szűkítése — így nem minden doboz. */
  taper?: number
}

function makeBox(spec: BoxSpec): THREE.Mesh {
  const [w, h, d] = spec.s
  let geo: THREE.BufferGeometry
  if (spec.taper !== undefined && spec.taper !== 1) {
    // Csonka gúla: a doboz felső lapját összehúzzuk. Ettől lesz „faragott"
    // a sziluett, nem Lego-szerű.
    geo = new THREE.CylinderGeometry(1, 1, 1, 4, 1)
    geo.rotateY(Math.PI / 4)
    const pos = geo.attributes.position as THREE.BufferAttribute
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i)
      const k = y > 0 ? spec.taper : 1
      pos.setX(i, pos.getX(i) * k * w * 0.707)
      pos.setZ(i, pos.getZ(i) * k * d * 0.707)
      pos.setY(i, y * h)
    }
    geo.computeVertexNormals()
  } else {
    geo = new THREE.BoxGeometry(w, h, d)
  }
  const m = new THREE.Mesh(geo, spec.emissive ? glow(spec.color) : mat(spec.color))
  m.position.set(spec.p[0], spec.p[1], spec.p[2])
  if (spec.r) m.rotation.set(spec.r[0], spec.r[1], spec.r[2])
  // Az önfényes részek (szem, energiamag) külön maradnak: a telegráf
  // animációja egyesével skálázza őket.
  if (!spec.emissive) {
    m.userData.mergeable = true
    m.userData.color = spec.color
  }
  return m
}

/**
 * Egy rig-csomópont összes merge-elhető doboza egyetlen geometriává.
 * A csomópont saját transzformációját NEM sütjük be — az animáció azt forgatja.
 */
function bakeGroup(group: THREE.Group): void {
  const parts: THREE.BufferGeometry[] = []
  const keep: THREE.Object3D[] = []

  for (const child of [...group.children]) {
    if (child instanceof THREE.Mesh && child.userData.mergeable) {
      const geo = child.geometry.clone()
      child.updateMatrix()
      geo.applyMatrix4(child.matrix)
      // Az attribútumkészletnek egyeznie kell a merge-hez.
      geo.deleteAttribute('uv')

      const c = new THREE.Color(child.userData.color as number)
      const n = geo.attributes.position.count
      const colors = new Float32Array(n * 3)
      for (let i = 0; i < n; i++) {
        colors[i * 3] = c.r
        colors[i * 3 + 1] = c.g
        colors[i * 3 + 2] = c.b
      }
      geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))

      parts.push(geo)
      child.geometry.dispose()
    } else {
      keep.push(child)
    }
  }

  if (parts.length === 0) return

  group.clear()
  for (const k of keep) group.add(k)

  const merged = mergeGeometries(parts, false)
  for (const g of parts) g.dispose()
  if (merged) {
    const mesh = new THREE.Mesh(merged, SOLID_MATERIAL)
    mesh.name = `${group.name}:merged`
    group.add(mesh)
  }
}

/** A teljes rig minden csoportjának összefűzése. */
function bakeRig(root: THREE.Object3D): void {
  const groups: THREE.Group[] = []
  root.traverse((o) => {
    if (o instanceof THREE.Group) groups.push(o)
  })
  // Alulról felfelé, hogy a gyerekcsoportok már készen legyenek.
  for (let i = groups.length - 1; i >= 0; i--) bakeGroup(groups[i])
}

function group(name: string, ...children: THREE.Object3D[]): THREE.Group {
  const g = new THREE.Group()
  g.name = name
  for (const c of children) g.add(c)
  return g
}

// ---------------------------------------------------------------------------
// Modell-interfész
// ---------------------------------------------------------------------------

export interface ModelAnimState {
  /** Vízszintes sebesség egység/mp — a járásciklus hajtja. */
  speed: number
  /** 0..1 — támadás előtti telegráf feltöltöttsége. */
  telegraph: number
  /** 0..1 — fájdalom-visszarándulás. */
  pain: number
  /** 0..1 — haláldőlés. */
  death: number
  /** Eltelt idő mp-ben (fáziseltolással típusonként). */
  time: number
}

export interface ActorModel {
  root: THREE.Group
  update(state: ModelAnimState): void
  dispose(): void
}

// ---------------------------------------------------------------------------
// Humanoid váz — grunt, imp, sentinel, brute közös alapja
// ---------------------------------------------------------------------------

interface HumanoidSpec {
  height: number
  /** Váll-szélesség a magassághoz képest — ez a sziluett fő hordozója. */
  shoulder: number
  hip: number
  headSize: number
  /** Előredőlés radiánban — a hajlott hátú lény máshogy olvasódik. */
  hunch: number
  bodyColor: number
  limbColor: number
  accentColor: number
  /** Szem/energia szín. */
  eyeColor: number
  /** Karhossz szorzó. */
  armLength: number
  /** Van-e vállra szerelt fegyver (sentinel, brute). */
  cannon: 'none' | 'shoulder' | 'arm'
  /** Fejdísz. */
  crest: 'helmet' | 'horns' | 'visor' | 'none'
  /** Lépésciklus frekvencia-szorzója. */
  gait: number
}

interface Rig {
  root: THREE.Group
  body: THREE.Group
  head: THREE.Group
  armL: THREE.Group
  armR: THREE.Group
  legL: THREE.Group
  legR: THREE.Group
  eyes: THREE.Mesh[]
  core: THREE.Mesh | null
}

function assembleHumanoid(s: HumanoidSpec): Rig {
  const H = s.height
  const legLen = H * 0.42
  const torsoH = H * 0.36
  const headH = H * s.headSize

  // --- Lábak (a csípőnél forognak) ---
  const mkLeg = (side: number): THREE.Group => {
    const g = group(`leg${side}`)
    g.position.set(side * s.hip * H * 0.5, legLen, 0)
    g.add(
      makeBox({ s: [H * 0.12, legLen * 0.55, H * 0.13], p: [0, -legLen * 0.28, 0], color: s.limbColor, taper: 0.85 }),
    )
    const shin = makeBox({
      s: [H * 0.1, legLen * 0.5, H * 0.11],
      p: [0, -legLen * 0.75, 0],
      color: s.limbColor,
      taper: 0.9,
    })
    g.add(shin)
    // Lábfej — kis dolog, de nélküle a figura „lebeg".
    g.add(makeBox({ s: [H * 0.11, H * 0.045, H * 0.2], p: [0, -legLen + H * 0.02, H * 0.04], color: s.accentColor }))
    return g
  }
  const legL = mkLeg(-1)
  const legR = mkLeg(1)

  // --- Törzs ---
  const body = group('body')
  body.position.y = legLen
  body.rotation.x = s.hunch

  body.add(
    makeBox({
      s: [s.shoulder * H, torsoH, s.shoulder * H * 0.6],
      p: [0, torsoH * 0.5, 0],
      color: s.bodyColor,
      taper: 1.18,
    }),
  )
  // Mellkasi páncéllap — hangsúlyos, sötétebb sáv, ami megtöri a felületet.
  body.add(
    makeBox({
      s: [s.shoulder * H * 0.72, torsoH * 0.34, s.shoulder * H * 0.2],
      p: [0, torsoH * 0.62, s.shoulder * H * 0.3],
      color: s.accentColor,
    }),
  )

  // --- Fej ---
  const head = group('head')
  head.position.y = torsoH
  head.add(makeBox({ s: [headH * 0.9, headH, headH * 0.9], p: [0, headH * 0.5, 0], color: s.bodyColor, taper: 0.8 }))

  const eyes: THREE.Mesh[] = []
  const eyeY = headH * 0.55
  const eyeZ = headH * 0.46
  if (s.crest === 'visor') {
    const visor = makeBox({
      s: [headH * 0.78, headH * 0.2, headH * 0.14],
      p: [0, eyeY, eyeZ],
      color: s.eyeColor,
      emissive: true,
    })
    head.add(visor)
    eyes.push(visor)
  } else {
    for (const side of [-1, 1]) {
      const e = makeBox({
        s: [headH * 0.2, headH * 0.16, headH * 0.12],
        p: [side * headH * 0.24, eyeY, eyeZ],
        color: s.eyeColor,
        emissive: true,
      })
      head.add(e)
      eyes.push(e)
    }
  }

  if (s.crest === 'helmet') {
    head.add(makeBox({ s: [headH * 1.02, headH * 0.34, headH * 1.0], p: [0, headH * 0.92, 0], color: s.accentColor, taper: 0.7 }))
  } else if (s.crest === 'horns') {
    for (const side of [-1, 1]) {
      head.add(
        makeBox({
          s: [headH * 0.14, headH * 0.85, headH * 0.14],
          p: [side * headH * 0.42, headH * 0.95, -headH * 0.1],
          color: s.accentColor,
          r: [-0.35, 0, side * 0.5],
          taper: 0.25,
        }),
      )
    }
  }

  // --- Karok ---
  const armLen = H * 0.34 * s.armLength
  const mkArm = (side: number): THREE.Group => {
    const g = group(`arm${side}`)
    g.position.set(side * s.shoulder * H * 0.58, torsoH * 0.86, 0)
    g.add(makeBox({ s: [H * 0.1, armLen * 0.55, H * 0.11], p: [0, -armLen * 0.28, 0], color: s.limbColor, taper: 0.85 }))
    g.add(makeBox({ s: [H * 0.09, armLen * 0.5, H * 0.1], p: [0, -armLen * 0.75, 0], color: s.limbColor, taper: 0.9 }))
    return g
  }
  const armL = mkArm(-1)
  const armR = mkArm(1)

  let core: THREE.Mesh | null = null
  if (s.cannon === 'arm') {
    const barrel = makeBox({
      s: [H * 0.16, H * 0.16, armLen * 0.7],
      p: [0, -armLen * 0.95, armLen * 0.28],
      color: s.accentColor,
    })
    armR.add(barrel)
    core = makeBox({
      s: [H * 0.09, H * 0.09, H * 0.05],
      p: [0, -armLen * 0.95, armLen * 0.6],
      color: s.eyeColor,
      emissive: true,
    })
    armR.add(core)
  } else if (s.cannon === 'shoulder') {
    const mount = makeBox({
      s: [H * 0.2, H * 0.18, H * 0.42],
      p: [s.shoulder * H * 0.62, torsoH * 1.02, H * 0.06],
      color: s.accentColor,
    })
    body.add(mount)
    core = makeBox({
      s: [H * 0.08, H * 0.08, H * 0.05],
      p: [s.shoulder * H * 0.62, torsoH * 1.02, H * 0.28],
      color: s.eyeColor,
      emissive: true,
    })
    body.add(core)
  }

  body.add(head)
  body.add(armL)
  body.add(armR)

  const root = group('actor', legL, legR, body)
  return { root, body, head, armL, armR, legL, legR, eyes, core }
}

// ---------------------------------------------------------------------------
// Négylábú váz — hound
// ---------------------------------------------------------------------------

function assembleQuadruped(): Rig {
  const H = 1.1
  const bodyLen = H * 1.35
  const legLen = H * 0.45

  const legs: THREE.Group[] = []
  const mkLeg = (x: number, z: number): THREE.Group => {
    const g = group('leg')
    g.position.set(x, legLen, z)
    g.add(makeBox({ s: [H * 0.11, legLen * 0.6, H * 0.12], p: [0, -legLen * 0.3, 0], color: 0x6b3d1e, taper: 0.8 }))
    g.add(makeBox({ s: [H * 0.09, legLen * 0.5, H * 0.1], p: [0, -legLen * 0.75, 0], color: 0x4c2915 }))
    legs.push(g)
    return g
  }

  const body = group('body')
  body.position.y = legLen
  body.add(makeBox({ s: [H * 0.5, H * 0.42, bodyLen], p: [0, H * 0.18, 0], color: 0x812739, taper: 1.1 }))
  // Tarajos hátgerinc — messziről is felismerhető sziluett.
  for (let i = 0; i < 5; i++) {
    body.add(
      makeBox({
        s: [H * 0.05, H * 0.16 - i * 0.012, H * 0.09],
        p: [0, H * 0.42, bodyLen * 0.3 - i * bodyLen * 0.16],
        color: 0x45141e,
        taper: 0.2,
      }),
    )
  }

  const head = group('head')
  head.position.set(0, H * 0.24, bodyLen * 0.52)
  head.add(makeBox({ s: [H * 0.34, H * 0.3, H * 0.42], p: [0, 0, H * 0.1], color: 0xa4374c, taper: 0.75 }))
  // Előreugró állkapocs — ez teszi agresszívvá a fejformát.
  head.add(makeBox({ s: [H * 0.26, H * 0.11, H * 0.3], p: [0, -H * 0.13, H * 0.24], color: 0x611c2a }))
  const eyes: THREE.Mesh[] = []
  for (const side of [-1, 1]) {
    const e = makeBox({
      s: [H * 0.07, H * 0.05, H * 0.04],
      p: [side * H * 0.11, H * 0.06, H * 0.3],
      color: 0xff3b1f,
      emissive: true,
    })
    head.add(e)
    eyes.push(e)
  }
  body.add(head)

  const root = group(
    'actor',
    mkLeg(-H * 0.2, bodyLen * 0.32),
    mkLeg(H * 0.2, bodyLen * 0.32),
    mkLeg(-H * 0.2, -bodyLen * 0.32),
    mkLeg(H * 0.2, -bodyLen * 0.32),
    body,
  )

  return {
    root,
    body,
    head,
    armL: legs[0],
    armR: legs[1],
    legL: legs[2],
    legR: legs[3],
    eyes,
    core: null,
  }
}

// ---------------------------------------------------------------------------
// Típus-katalógus
// ---------------------------------------------------------------------------

const R = RAMPS

const HUMANOIDS: Record<string, HumanoidSpec> = {
  /** Őrszem: emberi arány, katonás, keskeny. A „normál" viszonyítási pont. */
  grunt: {
    height: 1.75,
    shoulder: 0.3,
    hip: 0.22,
    headSize: 0.14,
    hunch: 0.05,
    bodyColor: R.stone[4],
    limbColor: R.stone[3],
    accentColor: R.rust[4],
    eyeColor: 0xff3b1f,
    armLength: 1,
    cannon: 'none',
    crest: 'helmet',
    gait: 1,
  },
  /** Tűzvető: hajlott hát, hosszú kar, szarvak — leginkább „démoni". */
  imp: {
    height: 1.9,
    shoulder: 0.34,
    hip: 0.2,
    headSize: 0.13,
    hunch: 0.28,
    bodyColor: R.flesh[6],
    limbColor: R.flesh[5],
    accentColor: R.flesh[3],
    eyeColor: 0xffd98a,
    armLength: 1.35,
    cannon: 'none',
    crest: 'horns',
    gait: 0.9,
  },
  /** Őrálló: magas, széles, vállra szerelt ágyú. Statikus fenyegetés. */
  sentinel: {
    height: 2.3,
    shoulder: 0.46,
    hip: 0.3,
    headSize: 0.11,
    hunch: 0,
    bodyColor: R.tech[5],
    limbColor: R.stone[4],
    accentColor: R.tech[3],
    eyeColor: 0x64c4cf,
    armLength: 0.9,
    cannon: 'shoulder',
    crest: 'visor',
    gait: 0.55,
  },
  /** Ostromgép: masszív, aránytalanul széles váll, karra szerelt ágyú. */
  brute: {
    height: 3.2,
    shoulder: 0.58,
    hip: 0.36,
    headSize: 0.09,
    hunch: 0.12,
    bodyColor: R.rust[5],
    limbColor: R.rust[4],
    accentColor: R.stone[4],
    eyeColor: 0xff3b1f,
    armLength: 1.1,
    cannon: 'arm',
    crest: 'none',
    gait: 0.45,
  },
}

/** Típusonkénti fáziseltolás, hogy egy csoport ne lépjen egyszerre. */
function phaseOf(type: string): number {
  let h = 0
  for (let i = 0; i < type.length; i++) h = (h * 31 + type.charCodeAt(i)) % 1000
  return (h / 1000) * Math.PI * 2
}

class RiggedModel implements ActorModel {
  readonly root: THREE.Group
  private rig: Rig
  private gait: number
  private phase: number
  private quad: boolean
  private baseEye: number[]

  constructor(rig: Rig, gait: number, phase: number, quad: boolean) {
    this.rig = rig
    this.root = rig.root
    this.gait = gait
    this.phase = phase
    this.quad = quad
    this.baseEye = rig.eyes.map((e) => e.scale.x)
  }

  update(st: ModelAnimState): void {
    const r = this.rig

    if (st.death > 0) {
      // Halál: előredőlés + összecsuklás. Egyetlen görbe, de olvasható.
      const d = Math.min(1, st.death)
      const ease = 1 - (1 - d) * (1 - d)
      r.root.rotation.x = ease * (Math.PI / 2) * 0.92
      r.root.position.y = -ease * 0.12
      r.body.rotation.x = ease * 0.4
      r.armL.rotation.x = ease * 1.4
      r.armR.rotation.x = ease * 1.2
      for (const e of r.eyes) e.scale.setScalar(Math.max(0.01, 1 - ease))
      return
    }

    const t = st.time * (this.quad ? 9 : 6) * this.gait + this.phase
    // A járásciklus amplitúdója a sebességgel nő, de telítődik — a rohanó
    // lény nem kalimpál groteszkül.
    const amp = Math.min(1, st.speed / 4.5)
    const swing = Math.sin(t) * amp

    if (this.quad) {
      // Átlós járás: bal-első és jobb-hátsó együtt.
      r.armL.rotation.x = swing * 0.85
      r.legR.rotation.x = swing * 0.85
      r.armR.rotation.x = -swing * 0.85
      r.legL.rotation.x = -swing * 0.85
      r.body.position.y = 0.45 * 1.1 + Math.abs(Math.sin(t)) * 0.05 * amp
      r.head.rotation.x = -0.1 + Math.sin(t * 2) * 0.05 * amp
    } else {
      r.legL.rotation.x = swing * 0.7
      r.legR.rotation.x = -swing * 0.7
      r.armL.rotation.x = -swing * 0.5
      r.armR.rotation.x = swing * 0.5
      // Függőleges ringás: a lépés ritmusa akkor is látszik, ha szemből nézed.
      r.body.position.y = r.body.userData.baseY + Math.abs(Math.sin(t)) * 0.045 * amp
    }

    // Telegráf: a támadás előtt felemeli a fegyvert/karját és felizzik.
    // Ez X kifejezett követelménye volt — nélküle olvashatatlan az AI.
    if (st.telegraph > 0) {
      const tg = st.telegraph
      const raise = tg * tg
      r.armR.rotation.x = -raise * 1.35
      if (!this.quad) r.armL.rotation.x = -raise * 0.5
      else r.head.rotation.x = -0.1 - raise * 0.5
      const pulse = 1 + Math.sin(st.time * 40) * 0.35 * tg
      for (let i = 0; i < r.eyes.length; i++) {
        r.eyes[i].scale.setScalar(this.baseEye[i] * (1 + tg * 0.9) * pulse)
      }
      if (r.core) r.core.scale.setScalar(1 + tg * 1.6 * pulse)
    } else {
      for (let i = 0; i < r.eyes.length; i++) r.eyes[i].scale.setScalar(this.baseEye[i])
      if (r.core) r.core.scale.setScalar(1)
    }

    // Fájdalom: rövid hátrarándulás.
    if (st.pain > 0) {
      r.body.rotation.x = (this.quad ? 0 : r.body.userData.baseRotX) - st.pain * 0.35
      r.root.position.y = -st.pain * 0.05
    } else {
      r.body.rotation.x = this.quad ? 0 : r.body.userData.baseRotX
      r.root.position.y = 0
      r.root.rotation.x = 0
    }
  }

  dispose(): void {
    this.root.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose()
    })
  }
}

/** Ellenségmodell építése típusnév alapján. */
export function buildEnemyModel(type: string): ActorModel {
  if (type === 'hound') {
    const rig = assembleQuadruped()
    rig.body.userData.baseY = rig.body.position.y
    rig.body.userData.baseRotX = 0
    bakeRig(rig.root)
    return new RiggedModel(rig, 1, phaseOf(type), true)
  }
  const spec = HUMANOIDS[type] ?? HUMANOIDS.grunt
  const rig = assembleHumanoid(spec)
  rig.body.userData.baseY = rig.body.position.y
  rig.body.userData.baseRotX = spec.hunch
  bakeRig(rig.root)
  return new RiggedModel(rig, spec.gait, phaseOf(type), false)
}

export function hasEnemyModel(type: string): boolean {
  return type === 'hound' || type in HUMANOIDS
}

// ---------------------------------------------------------------------------
// Felvehető tárgyak — ezek billboard helyett kis 3D testek, pörögve
// ---------------------------------------------------------------------------

export interface PickupVisual {
  root: THREE.Group
  /** A lebegés/pörgés fázisa, hogy egy csoport ne mozogjon szinkronban. */
  phase: number
  glow: THREE.PointLight | null
}

interface PickupSpec {
  build(): THREE.Object3D
  light?: number
}

const PICKUPS: Record<string, PickupSpec> = {
  medkitSmall: {
    build: () =>
      group(
        'medkit',
        makeBox({ s: [0.34, 0.22, 0.24], p: [0, 0, 0], color: R.stone[5] }),
        makeBox({ s: [0.16, 0.05, 0.26], p: [0, 0.05, 0], color: 0xff3b1f, emissive: true }),
        makeBox({ s: [0.05, 0.16, 0.26], p: [0, 0.05, 0], color: 0xff3b1f, emissive: true }),
      ),
    light: 0xff3b1f,
  },
  medkitLarge: {
    build: () =>
      group(
        'medkitL',
        makeBox({ s: [0.5, 0.34, 0.36], p: [0, 0, 0], color: R.stone[6] }),
        makeBox({ s: [0.24, 0.07, 0.38], p: [0, 0.06, 0], color: 0xff3b1f, emissive: true }),
        makeBox({ s: [0.07, 0.24, 0.38], p: [0, 0.06, 0], color: 0xff3b1f, emissive: true }),
      ),
    light: 0xff3b1f,
  },
  armor: {
    build: () =>
      group(
        'armor',
        makeBox({ s: [0.44, 0.5, 0.2], p: [0, 0, 0], color: R.tech[4], taper: 0.55 }),
        makeBox({ s: [0.18, 0.1, 0.22], p: [0, 0.12, 0], color: 0x64c4cf, emissive: true }),
      ),
    light: 0x64c4cf,
  },
  bullets: {
    build: () =>
      group(
        'bullets',
        makeBox({ s: [0.3, 0.14, 0.2], p: [0, 0, 0], color: R.rust[3] }),
        makeBox({ s: [0.26, 0.05, 0.16], p: [0, 0.08, 0], color: R.hazard[5] }),
      ),
  },
  shells: {
    build: () => {
      const g = group('shells')
      // Négy töltény egymás mellett — a forma önmagában is elárulja, mi az.
      for (let i = 0; i < 4; i++) {
        g.add(makeBox({ s: [0.075, 0.2, 0.075], p: [(i - 1.5) * 0.095, 0, 0], color: 0xa53347 }))
        g.add(makeBox({ s: [0.085, 0.06, 0.085], p: [(i - 1.5) * 0.095, -0.1, 0], color: R.hazard[5] }))
      }
      return g
    },
  },
  rockets: {
    build: () =>
      group(
        'rockets',
        makeBox({ s: [0.16, 0.16, 0.5], p: [0, 0, 0], color: R.stone[4] }),
        makeBox({ s: [0.16, 0.16, 0.12], p: [0, 0, 0.28], color: 0xff3b1f, taper: 0.3 }),
      ),
    light: 0xff3b1f,
  },
  cells: {
    build: () =>
      group(
        'cells',
        makeBox({ s: [0.26, 0.3, 0.26], p: [0, 0, 0], color: R.tech[2] }),
        makeBox({ s: [0.16, 0.22, 0.16], p: [0, 0, 0], color: 0x8affff, emissive: true }),
      ),
    light: 0x8affff,
  },
}

export function buildPickupModel(type: string): THREE.Object3D {
  const spec = PICKUPS[type]
  if (spec) {
    const obj = spec.build()
    bakeRig(obj)
    return obj
  }
  // Ismeretlen típus: feltűnő, hibát jelző kocka — ne csendben tűnjön el.
  return makeBox({ s: [0.3, 0.3, 0.3], p: [0, 0, 0], color: 0xff00ff, emissive: true })
}

export function pickupLightColor(type: string): number | null {
  return PICKUPS[type]?.light ?? null
}

/** Kulcskártya — mindhárom szín ugyanabból a formából, csak más árnyalattal. */
export function buildKeyModel(color: number): THREE.Object3D {
  return group(
    'key',
    makeBox({ s: [0.26, 0.36, 0.05], p: [0, 0, 0], color, emissive: true }),
    makeBox({ s: [0.3, 0.06, 0.07], p: [0, -0.14, 0], color: R.stone[3] }),
  )
}
